import Clipboard from 'clipboard';
import { isValueExist, showAuthLink } from '@util';
import { AssetService } from '@/services';
import { CONNECTION_TYPES } from '@consts';

import Dialog from '@components/Common/Dialog';
import CopyLink from '@components/Common/CopyLink';
import ConnectionBox from '@components/Common/ConnectionBox';
import PrevIcon from '../../../public/static/assets/img/svg_icons/prev.svg';

import styled, { css } from 'vue-styled-components';
import COLORS from '@design/colors.js';
import { darken } from 'polished';
import { applyHoverAndPress } from '@styles/common.styled';

import { mapActions, mapState, mapGetters } from 'vuex';
const { PIXEL } = CONNECTION_TYPES;

export default {
    name: 'PixelShareModal',
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        token: {
            type: [Number, String],
            required: true,
        },
    },
    mounted() {
        this.$nextTick(() => {
            var clipboard = new Clipboard(
                '.pixels-modal-wrapper .copy-target',
                {
                    text: R.always('525463574501100'),
                }
            );

            clipboard.on('success', () => this.toast(this.$t('COMMON.copied')));
            this.getBeforeDestroy$().subscribe(() => clipboard.destroy());
        });
    },
    destroyed() {
        this.removeFn();
    },
    data() {
        return {
            values: [],
            checked: [],
            compFacebookProfile: {},
            isWarning: false,
            howToPage: 0,
            selectedPixel: {},
            isPageSelected: false,
            isWarningModalActive: false,
            copyLink: false,
            loadingProgress: false,
            removeFn: R.identity,
        };
    },
    watch: {
        pages: {
            handler([pixel] = []) {
                if (isValueExist(pixel) && !isValueExist(this.selectedPixel)) {
                    this.selectedPixel = pixel;
                    this.radioSelected(pixel);
                }
            },
            immediate: true,
        },
    },
    computed: {
        ...mapGetters('proposal', ['facebookProfile']),
        ...mapGetters('user', ['fbInfo']),
        ...mapState('user', ['authCode']),
        _active: {
            get: R.prop('active'),
            set(value) {
                this.$emit('update:active', value);
            },
        },
        pages: R.pathOr([], ['fbInfo', 'pixels']),
        confirmText() {
            switch (this.status) {
                case 'noPages':
                    return this.$t('CONNECTION.try_with_another_account');
                case 'howTo':
                    if (this.howToPage === this.howTo.length - 1) {
                        return this.$t('COMMON.done');
                    } else {
                        return this.$t('COMMON.next');
                    }
                case 'tryAnother':
                    return this.$t('COMMON.close');
                case 'notAuth':
                    return this.$t('COMMON.login_to_facebook');
                case 'pages':
                    return this.$t('COMMON.select');
                default:
                    return '';
            }
        },
        lastHowTo() {
            return this.howToPage === R.dec(this.howTo.length);
        },
        howTo() {
            return this.$t('PROPOSAL.pixel_how_to');
        },
        currentHowTo() {
            return this.howTo[this.howToPage];
        },
        copyUrl() {
            return `${window.location.origin}/#/businessLink?token=${this.token}`;
        },
        status() {
            if (this.copyLink) {
                return 'tryAnother';
            } else if (!isValueExist(this.fbInfo)) {
                return 'notAuth';
            } else if (!this.pages.length) {
                return 'noPages';
            } else if (this.isPageSelected) {
                return 'howTo';
            } else {
                return 'pages';
            }
        },
        hasCancelButton: R.compose(
            R.not,
            R.test(/tryAnother|howTo/gi),
            R.prop('status')
        ),
        refinedValue() {
            return {
                ...this.selectedPixel,
                facebookUserToken: this.fbInfo.longLifeToken,
            };
        },
    },
    methods: {
        ...mapActions('user', ['fetchAssetThroughLink']),
        radioSelected(facebookPixel) {
            this.selectedPixel = facebookPixel;
            this.$emit('update', this.refinedValue);
        },
        confirm() {
            switch (this.status) {
                case 'noPages':
                    this.copyLink = true;
                    break;
                case 'tryAnother':
                    this._active = false;
                    break;
                case 'notAuth':
                    this.loadingProgress = true;
                    this.removeFn = showAuthLink({
                        type: PIXEL,
                        cb: (event) =>
                            this.fetchAssetThroughLink(event).finally(() => {
                                this.loadingProgress = false;
                                this.removeFn();
                            }),
                        cancelCb: () => (this.loadingProgress = false),
                    });
                    break;
                case 'pages':
                    if (isValueExist(this.selectedPixel)) {
                        this.loadingProgress = true;
                        this.checkAdded()
                            .then(
                                (results = []) => this.inputEmitter(results),
                                () => (this.isPageSelected = true)
                            )
                            .finally(() => (this.loadingProgress = false));
                    } else {
                        return this.toast('please select pixel');
                    }
                    return;
                case 'howTo':
                    if (this.howToPage === this.howTo.length - 1) {
                        this.loadingProgress = true;
                        return this.checkAdded()
                            .then(
                                (results = []) => this.inputEmitter(results),
                                () =>
                                    this.dialog({
                                        cancelText: this.t$t('COMMON.yes'),
                                        confirmText: this.$t(
                                            'PROPOSAL.confirm_pixel_confirm'
                                        ),
                                        text: this.$t(
                                            'PROPOSAL.confirm_pixel_sharing'
                                        ),
                                    })
                            )
                            .finally(() => (this.loadingProgress = false));
                    } else {
                        this.howToPage++;
                        this.loadingProgress = false;
                    }
            }
        },
        checkAdded() {
            return AssetService.addAssets({
                queryObj: R.pick(['token'], this),
                assets: [
                    {
                        type: PIXEL,
                        data: {
                            ...this.refinedValue,
                        },
                        code: this.authCode,
                    },
                ],
            });
        },
        pageDescriptionExtractor: R.compose(
            R.join(' | '),
            R.filter(R.identity),
            R.props(['', 'id'])
        ),
        renderByPages() {
            switch (this.status) {
                case 'notAuth': {
                    return (
                        <NotAuthContents>
                            <DescSpan>
                                {this.$t('PROPOSAL.select_pixel_desc')}
                            </DescSpan>
                        </NotAuthContents>
                    );
                }
                case 'noPages': {
                    return (
                        <NoPagesContents>
                            <DescSpan>
                                {this.$t('PROPOSAL.no_pixel_detected')}
                            </DescSpan>
                        </NoPagesContents>
                    );
                }
                case 'tryAnother': {
                    return (
                        <CopyLink
                            label={this.$t('CONNECTION.retry_with_ignito_mode')}
                            link={this.copyUrl}
                        />
                    );
                }
                case 'pages': {
                    return (
                        <PagesContents>
                            <DescSpan class="bottom-margin">
                                {this.$t('PROPOSAL.select_fb_pixel_ment')}
                            </DescSpan>
                            <PageElemsWrapper>
                                {this.pages.map((page = {}) => {
                                    const {
                                        id = '',
                                        disable_reason = '',
                                        enable,
                                    } = page;
                                    const disabled = !enable;

                                    const props = {
                                        attrs: {
                                            key: id,
                                            value: page,
                                            active:
                                                this.selectedPixel.id === id,

                                            nameExtractor: R.prop('name'),
                                            descriptionExtractor: this
                                                .pageDescriptionExtractor,
                                            disabled,
                                            ...(disabled &&
                                                disable_reason ===
                                                    'inactive_pixel' && {
                                                    disabledInfo: {
                                                        reasonText: this.$t(
                                                            `CONNECTION.error_${PIXEL}_${disable_reason}`
                                                        ),
                                                        type: 'inactive',
                                                        channel: PIXEL,
                                                    },
                                                }),
                                        },
                                        on: {
                                            click: () =>
                                                this.radioSelected(page),
                                        },
                                    };
                                    return <ConnectionBox {...props} />;
                                })}
                            </PageElemsWrapper>
                        </PagesContents>
                    );
                }
                case 'howTo': {
                    return (
                        <PixelHowTo>
                            <PixelTitle
                                domPropsInnerHTML={this.$t(
                                    this.currentHowTo.title,
                                    this.selectedPixel
                                )}
                            />
                            <ImgContainer>
                                {this.howtoPage > 0 && (
                                    <span
                                        class="prev adriel-flex-center"
                                        onClick={() => this.howToPage--}
                                    >
                                        <PrevIcon v-view-box />
                                    </span>
                                )}

                                <img src={this.currentHowTo.image} />
                                {!this.lastHowTo && (
                                    <span
                                        class="next adriel-flex-center"
                                        onClick={() => this.howToPage++}
                                    >
                                        <PrevIcon v-view-box />
                                    </span>
                                )}
                            </ImgContainer>
                            <Pages>
                                {this.howTo.map((_, index) => {
                                    return (
                                        <NavBtn
                                            key={index}
                                            active={index === this.howToPage}
                                            onClick={() =>
                                                (this.howToPage = index)
                                            }
                                        />
                                    );
                                })}
                            </Pages>
                        </PixelHowTo>
                    );
                }
            }
        },
    },
    render() {
        const props = {
            attrs: {
                active: this._active,
                hasCancelButton: this.hasCancelButton,
                confirmText: this.confirmText,
                loadingProgress: this.loadingProgress,
            },
            on: {
                confirm: this.confirm,
                cancel: () => (this._active = false),
            },
        };
        return (
            <Dialog {...props}>
                <div>
                    <ModalWrapper ref="wrapper">
                        <ModalBody>{this.renderByPages()}</ModalBody>
                    </ModalWrapper>
                </div>
            </Dialog>
        );
    },
};

const NavBtn = styled('span', { active: Boolean })`
    margin: 0 3.5px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    cursor: pointer;
    ${({ active }) =>
        active
            ? css`
                  background-color: ${darken(0.1, COLORS['blue-60'])};
              `
            : css`
                  ${applyHoverAndPress(COLORS['gray-15'], 'background-color')};
              `}
`;

const ModalWrapper = styled.div`
    position: relative;
    background-color: white;
    border-radius: 10px;
`;

const ModalBody = styled.div`
    & > * {
        max-width: 480px;
    }
`;

const DescSpan = styled.span`
    font-size: 16px;
    color: #080a0d;
    width: 100%;
    word-break: keep-all;
    word-wrap: break-word;
    &.bottom-margin {
        margin-bottom: 20px;
    }
`;

const NotAuthContents = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 450px;
`;

const NoPagesContents = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const PagesContents = styled.div`
    display: flex;
    flex-direction: column;
`;

const PageElemsWrapper = styled.div`
    width: 100%;
    margin: -6px 0;
    overflow-y: visible;
    overflow-x: visible;
    display: flex;
    flex-direction: column;
`;

const PixelHowTo = styled.div`
    width: 820px;
    max-width: 820px;
    @media screen and (max-width: 767px) {
        width: auto;
        max-width: auto;
    }
`;

const PixelTitle = styled.span`
    font-size: 16px;
    color: #080a0d;
    width: 100%;
    word-break: keep-all;
    word-wrap: break-word;

    a {
        text-decoration: underline;
    }

    a:hover {
        color: $color21;
    }

    b {
        font-weight: bold;
    }

    .copy {
        padding: 5px 7px;
        margin-left: 5px;
        margin-right: 7px;
        border-radius: 3px;
        background-color: $color3;
        font-size: 11px;
        color: $color1;
        cursor: pointer;
    }
`;

const ImgContainer = styled.div`
    position: relative;
    & > img {
        width: 100%;
    }
    & > span {
        position: absolute;
        top: 50%;
        margin-top: -18px;
        cursor: pointer;
        width: 48px;
        height: 48px;
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 50%;
        transition: background-color 0.2s;

        &:hover {
            background-color: rgba(26, 26, 26, 0.2);
        }

        &:active {
            background-color: rgba(51, 51, 51, 0.2);
        }

        svg {
            fill: white;
        }

        &.prev {
            left: 15px;
        }
        &.next {
            right: 15px;
            svg {
                transform: rotate(180deg);
            }
        }
    }
    margin: 15px 0;
`;

const Pages = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;
